if (!customElements.get('page-campaign-picks')) {
  customElements.define('page-campaign-picks', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.groups = [...this.querySelectorAll('[data-campaign-group]')];
      const menu = this.querySelector('[role="tablist"]');
      menu.replaceChildren();
      let tabTrack = menu;
      if (this.classList.contains('page-campaign-picks--products')) {
        tabTrack = document.createElement('div');
        tabTrack.className = 'page-campaign-picks__tab-track';
        tabTrack.setAttribute('role', 'presentation');
        menu.append(tabTrack);
      }
      this.tabGroups = this.groups.filter(group => group.dataset.label.trim());
      this.tabs = this.tabGroups.map((group, index) => {
        const tab = document.createElement('button');
        tab.type = 'button'; tab.role = 'tab'; tab.textContent = group.dataset.label;
        tab.id = `CampaignTab-${this.dataset.sectionId}-${group.dataset.campaignGroup}`;
        group.id = `CampaignPanel-${this.dataset.sectionId}-${group.dataset.campaignGroup}`;
        group.setAttribute('role', 'tabpanel'); group.setAttribute('aria-labelledby', tab.id);
        tab.setAttribute('aria-controls', group.id);
        tab.addEventListener('click', () => this.select(index), { signal });
        tab.addEventListener('keydown', event => {
          let next;
          if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
          if (event.key === 'ArrowLeft') next = (index - 1 + this.tabs.length) % this.tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = this.tabs.length - 1;
          if (next !== undefined) { event.preventDefault(); this.select(next); this.tabs[next].focus(); }
        }, { signal });
        tabTrack.append(tab);
        return tab;
      });
      this.groups.forEach(group => {
        if (!group.dataset.label.trim()) { group.setAttribute('role', 'region'); group.setAttribute('aria-label', 'Products'); }
        const rail = group.querySelector('.page-campaign-picks__rail');
        const update = () => this.updateNavigation(group);
        rail.addEventListener('scroll', update, { passive: true, signal });
        group.querySelector('[data-campaign-prev]').addEventListener('click', () => rail.scrollBy({ left: -this.scrollDistance(rail), behavior: this.motion() }), { signal });
        group.querySelector('[data-campaign-next]').addEventListener('click', async () => {
          if (rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 4 && rail.dataset.nextUrl) await this.loadMore(group);
          rail.scrollBy({ left: this.scrollDistance(rail), behavior: this.motion() });
        }, { signal });
        group.querySelector('[data-campaign-more]').addEventListener('click', () => this.loadMore(group), { signal });
      });
      menu.hidden = this.tabs.length === 0;
      this.classList.add('is-enhanced');
      this.select(0, false);
      this.observeBadges(this);
      this.resizeObserver?.disconnect();
      this.resizeObserver = new ResizeObserver(() => this.groups.forEach(group => this.updateNavigation(group)));
      this.resizeObserver.observe(this);
      document.addEventListener('shopify:block:select', event => {
        if (!this.contains(event.target)) return;
        const group = event.target.closest('[data-campaign-group]');
        const index = this.tabGroups.indexOf(group);
        if (index >= 0) this.select(index);
      }, { signal });
    }
    disconnectedCallback() { this.controller?.abort(); this.resizeObserver?.disconnect(); clearTimeout(this.panelAnimationTimer); }
    scrollDistance(rail) {
      const card = rail.querySelector(".page-campaign-card__card");
      const style = getComputedStyle(rail);
      return (card?.getBoundingClientRect().width || rail.clientWidth * .85) + (parseFloat(style.columnGap || style.gap) || 0);
    }
    motion() { return matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'; }
    select(index, center = true) {
      const previous = this.activeIndex;
      clearTimeout(this.panelAnimationTimer);
      this.groups.forEach((group, i) => {
        group.classList.remove('is-entering-from-right', 'is-entering-from-left');
        group.hidden = Boolean(group.dataset.label.trim()) && group !== this.tabGroups[index];
      });
      const panel = this.tabGroups[index];
      if (center && previous !== undefined && previous !== index && panel) {
        void panel.offsetWidth;
        const direction = previous <= index ? 'is-entering-from-right' : 'is-entering-from-left';
        panel.classList.add(direction);
        this.panelAnimationTimer = setTimeout(() => panel.classList.remove(direction), 380);
      }
      this.activeIndex = index;
      this.tabs.forEach((tab, i) => { tab.setAttribute('aria-selected', String(i === index)); tab.tabIndex = i === index ? 0 : -1; });
      const tab = this.tabs[index];
      if (center && tab) {
        const menu = tab.closest('[role="tablist"]');
        const rect = tab.getBoundingClientRect(); const parent = menu.getBoundingClientRect();
        menu.scrollBy({ left: rect.left + rect.width / 2 - parent.left - parent.width / 2, behavior: this.motion() });
      }
      this.groups.forEach(group => this.updateNavigation(group));
    }
    updateNavigation(group) {
      const rail = group.querySelector('.page-campaign-picks__rail');
      const maxScrollLeft = Math.max(0, rail.scrollWidth - rail.clientWidth);
      const overflow = maxScrollLeft > 4;
      const prev = group.querySelector('[data-campaign-prev]'); const next = group.querySelector('[data-campaign-next]');
      if (this.dataset.layout !== 'slider' || matchMedia('(max-width: 989.98px)').matches || group.hidden) {
        prev.hidden = next.hidden = true;
        return;
      }
      prev.hidden = !overflow || rail.scrollLeft <= 4;
      next.hidden = !rail.dataset.nextUrl && (!overflow || rail.scrollLeft >= maxScrollLeft - 4);
      prev.disabled = next.disabled = false;
    }
    observeBadges(root) {
      root.querySelectorAll('[data-campaign-badge-image]').forEach(img => {
        const update = () => img.closest('[data-campaign-badge]').classList.toggle('has-image', img.complete && img.naturalWidth > 0);
        img.addEventListener('load', update, { signal: this.controller.signal });
        img.addEventListener('error', update, { signal: this.controller.signal });
        update();
      });
    }
    async loadMore(group) {
      const rail = group.querySelector('.page-campaign-picks__rail');
      if (!rail.dataset.nextUrl || rail.dataset.loading === 'true') return;
      const more = group.querySelector('[data-campaign-more]'); const status = group.querySelector('[data-campaign-status]');
      rail.dataset.loading = 'true'; more.disabled = true; status.textContent = 'Loading…';
      try {
        const url = new URL(rail.dataset.nextUrl, location.href);
        if (url.origin !== location.origin) throw new Error('Invalid collection page');
        url.searchParams.set('section_id', this.dataset.sectionId);
        const response = await fetch(url, { signal: this.controller.signal });
        if (!response.ok) throw new Error('Unable to load products');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const nextGroup = [...doc.querySelectorAll('[data-campaign-group]')].find(g => g.dataset.campaignGroup === group.dataset.campaignGroup);
        const nextRail = nextGroup?.querySelector('.page-campaign-picks__rail');
        const cards = nextRail?.querySelectorAll('.page-campaign-card__card');
        if (!cards?.length) throw new Error('Unable to load products');
        cards.forEach(card => rail.append(document.importNode(card, true)));
        rail.dataset.nextUrl = nextRail.dataset.nextUrl || '';
        rail.hydrateFallbackCodes?.(); this.observeBadges(rail);
        more.hidden = !rail.dataset.nextUrl; status.textContent = '';
      } catch (error) {
        if (error.name !== 'AbortError') status.textContent = 'Unable to load products. Please try again.';
      } finally {
        rail.dataset.loading = 'false'; more.disabled = false; this.updateNavigation(group);
      }
    }
  });
}
